import React, { useEffect, useState } from 'react';
import { Card, Typography, Button, Spin, message, Input, Popconfirm } from 'antd';
import { ArrowLeftOutlined, BookOutlined, SaveOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api, TongueLabelInfo } from '../utils/api';

const { Title, Text } = Typography;
const { TextArea } = Input;

const TongueLabelPage: React.FC = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const [labels, setLabels] = useState<TongueLabelInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingLabel, setSavingLabel] = useState<string | null>(null);

  // 检查用户是否已登录且为医生
  useEffect(() => {
    if (!authState.isLoading && !authState.isAuthenticated) {
      navigate('/login');
    }
    if (!authState.isLoading && authState.isAuthenticated && !authState.user?.is_doctor) {
      message.error('您没有权限访问此页面');
      navigate('/');
    }
  }, [authState.isLoading, authState.isAuthenticated, authState.user, navigate]);

  // 加载标签释义配置
  const loadLabels = async () => {
    if (!authState.token) {
      return;
    }

    try {
      setLoading(true);
      const response = await api.getTongueLabels(authState.token);

      if (response.status === 200 && response.data) {
        setLabels(response.data.labels || []);
      } else {
        message.error(response.msg || '获取舌象解读配置失败');
      }
    } catch (error) {
      console.error('获取舌象解读配置失败:', error);
      message.error('获取舌象解读配置失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authState.isAuthenticated && authState.token && authState.user?.is_doctor) {
      loadLabels();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState.isAuthenticated, authState.token, authState.user]);

  // 更新本地编辑内容
  const handleFieldChange = (label: string, field: 'description' | 'suggestion', value: string) => {
    setLabels((prev) =>
      prev.map((item) => (item.label === label ? { ...item, [field]: value } : item))
    );
  };

  // 保存单个标签的释义配置
  const handleSave = async (item: TongueLabelInfo) => {
    if (!authState.token) {
      message.error('请先登录');
      return;
    }

    try {
      setSavingLabel(item.label);
      const response = await api.updateTongueLabel(
        {
          label: item.label,
          description: item.description,
          suggestion: item.suggestion,
        },
        authState.token
      );

      if (response.status === 200) {
        message.success(`「${item.label}」解读已更新`);
      } else {
        message.error(response.msg || '保存失败');
      }
    } catch (error) {
      console.error('保存舌象解读失败:', error);
      message.error('保存失败');
    } finally {
      setSavingLabel(null);
    }
  };

  // 如果正在加载
  if (authState.isLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <Spin size="large" />
      </div>
    );
  }

  // 如果未登录或不是医生
  if (!authState.isAuthenticated || !authState.user?.is_doctor) {
    return null;
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '40px 20px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center'
    }}>
      {/* 顶部导航栏 */}
      <div style={{
        width: '100%',
        maxWidth: '1000px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '40px'
      }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/')}
          size="large"
          style={{
            background: 'rgba(255, 255, 255, 0.2)',
            border: 'none',
            color: 'white'
          }}
        >
          返回主页
        </Button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <BookOutlined style={{ fontSize: '32px', color: 'white' }} />
          <Title level={2} style={{ color: 'white', margin: 0 }}>
            舌象解读管理
          </Title>
        </div>
        <div style={{ width: '100px' }} /> {/* 占位符保持居中 */}
      </div>

      {/* 说明 */}
      <div style={{ width: '100%', maxWidth: '1000px', marginBottom: '24px' }}>
        <Card style={{ borderRadius: '8px' }}>
          <Text>
            舌象标签共 22 类（由识别模型固定，不可增删），此处可编辑每类标签向用户展示的「意义解读」与「调养建议」文案，保存后立即生效。
          </Text>
        </Card>
      </div>

      {/* 标签释义列表 */}
      <div style={{ width: '100%', maxWidth: '1000px' }}>
        <Spin spinning={loading}>
          {labels.map((item) => (
            <Card
              key={item.label}
              title={<span style={{ color: '#cf1322', fontSize: '16px' }}>{item.label}</span>}
              style={{
                borderRadius: '12px',
                marginBottom: '16px',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
              }}
              extra={
                <Popconfirm
                  title="确定保存这条解读文案吗？"
                  onConfirm={() => handleSave(item)}
                  okText="保存"
                  cancelText="取消"
                >
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    loading={savingLabel === item.label}
                  >
                    保存
                  </Button>
                </Popconfirm>
              }
            >
              <div style={{ marginBottom: '12px' }}>
                <Text strong>意义解读：</Text>
                <TextArea
                  value={item.description}
                  onChange={(e) => handleFieldChange(item.label, 'description', e.target.value)}
                  autoSize={{ minRows: 2, maxRows: 6 }}
                  style={{ marginTop: '8px' }}
                />
              </div>
              <div>
                <Text strong>调养建议：</Text>
                <TextArea
                  value={item.suggestion}
                  onChange={(e) => handleFieldChange(item.label, 'suggestion', e.target.value)}
                  autoSize={{ minRows: 2, maxRows: 6 }}
                  style={{ marginTop: '8px' }}
                />
              </div>
            </Card>
          ))}
        </Spin>
      </div>
    </div>
  );
};

export default TongueLabelPage;
